import { Box, Stack, TextField } from '@mui/material';
import { ChangeEvent, FC } from 'react';
import strings from '../../../../common/Translation/Translate';

const DEFAULT_COLOR = '#66BB6A';

export const normalizeHexColor = (value: string): string => {
  const trimmed = value.trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return trimmed.toUpperCase();
  }
  if (/^[0-9A-Fa-f]{6}$/.test(trimmed)) {
    return `#${trimmed.toUpperCase()}`;
  }
  return DEFAULT_COLOR;
};

export const isValidHexColor = (value: string): boolean => {
  const trimmed = value.trim();
  return /^#?[0-9A-Fa-f]{6}$/.test(trimmed);
};

type Props = {
  value: string;
  onChange: (color: string) => void;
};

const PartOfDayColorPicker: FC<Props> = ({ value, onChange }) => {
  const pickerValue = normalizeHexColor(value);

  const handlePickerChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value.toUpperCase());
  };

  const handleHexChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleHexBlur = () => {
    if (isValidHexColor(value)) {
      onChange(normalizeHexColor(value));
    }
  };

  return (
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
      <Box
        sx={{
          position: 'relative',
          width: 48,
          height: 48,
          flexShrink: 0,
          borderRadius: 1.5,
          overflow: 'hidden',
          border: '2px solid',
          borderColor: 'divider',
          boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.25)',
          bgcolor: pickerValue,
          cursor: 'pointer',
          transition: 'box-shadow 0.2s ease',
          '&:hover': {
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.25)',
          },
        }}>
        <Box
          component="input"
          type="color"
          value={pickerValue}
          onChange={handlePickerChange}
          aria-label={strings.partOfDay_field_color}
          sx={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            opacity: 0,
            cursor: 'pointer',
            border: 'none',
            p: 0,
          }}
        />
      </Box>
      <TextField
        size="small"
        fullWidth
        label={strings.partOfDay_color_hex_label}
        value={value}
        onChange={handleHexChange}
        onBlur={handleHexBlur}
        placeholder="#66BB6A"
        inputProps={{ maxLength: 7, style: { fontFamily: 'monospace' } }}
      />
    </Stack>
  );
};

export default PartOfDayColorPicker;
